import { ExperienceName } from './constants';
import type { ExperienceNameType } from './constants';
import { OwningTeam } from './owningTeamConstants';

/** Every registered experience must choose a confirmed team or explicitly use Unassigned. */
const DEFAULT_TEAM_FOR_EXPERIENCE: Record<ExperienceNameType, OwningTeam> = {
	// Experiences owned by the staging-area-modal and smart-creation areas
	[ExperienceName.AIFC_FINALIZE_CONTENT]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_ALL_DATA_HANDLED]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_MESSAGE]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_LOAD_STAGING_AREA_MODAL]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_LOAD_CHAT_ONLY_CREATION_MODAL]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_DEEP_LINK_LAUNCH]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_GENERATE_CONTENT]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_GENERATE_CONTENT_IN_CHAT]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_INITIAL_DATA_RECEIVED]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_MODAL_SESSION]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_UPDATE_CONTENT]: OwningTeam.AifcFoundations,
	[ExperienceName.UPDATE_CONTENT_IN_CHAT]: OwningTeam.EditorAi,
	[ExperienceName.PREVIEW_CARD_RENDER]: OwningTeam.AifcFoundations,
	[ExperienceName.PREVIEW_CONTENT]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_LOAD_DRAFTS_SECTION]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_LOAD_PROMPT_SUGGESTIONS]: OwningTeam.AifcFoundations,
	[ExperienceName.AIFC_TWC_ONBOARDING_MODAL_LAUNCH]: OwningTeam.AifcFoundations,
	// Remix experiences
	[ExperienceName.REMIX_START]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_AIFC_HANDOFF]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_RENDERS_IN_MODAL]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_CREATE]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_OPEN_MENU]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_SELECT_OPTION]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_SHOW_LOADING]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_VIEW]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_VIEW_LIMITED]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_REGENERATE]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_INSERT]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_INSERT_LIMITED]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_PASTE]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_COPY_FROM_PREVIEW]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_DIAGRAM_PASTE_FINALIZE]: OwningTeam.CcCeMau,
	[ExperienceName.REMIX_DUPLICATE_APP]: OwningTeam.CcCeMau,
	// Chat, prompt input, skills, and For You experiences
	[ExperienceName.ANSWER_NOW]: OwningTeam.RovoChat,
	[ExperienceName.BULK_RESOLVE_CONVERSATION_ACTIONS]: OwningTeam.RovoChat,
	[ExperienceName.CLEAR_CHAT]: OwningTeam.RovoChat,
	[ExperienceName.CREATE_SEEDED_CONVERSATION_CHANNEL]: OwningTeam.RovoChat,
	[ExperienceName.FETCH_ARTIFACT_LIBRARY]: OwningTeam.RovoChat,
	[ExperienceName.FETCH_CONTEXTUAL_STARTERS]: OwningTeam.RovoChat,
	[ExperienceName.FETCH_DYNAMIC_CONVERSATION_STARTERS]: OwningTeam.RovoChat,
	[ExperienceName.FETCH_TASKS]: OwningTeam.RovoChat,
	[ExperienceName.GET_AUTO_APPLY_STATE]: OwningTeam.RovoChat,
	[ExperienceName.GET_CONVERSATION_CHANNELS_INITIAL_LOAD]: OwningTeam.RovoChat,
	[ExperienceName.HYDRATE_CONVERSATION]: OwningTeam.RovoChat,
	[ExperienceName.HYDRATE_SKILL_METADATA]: OwningTeam.RovoChat,
	[ExperienceName.LOAD_INLINE_EDITOR]: OwningTeam.RovoChat,
	[ExperienceName.LOAD_MORE_CONVERSATION_CHANNELS]: OwningTeam.RovoChat,
	[ExperienceName.LOAD_PANEL_COMPOSABLE_EDITOR]: OwningTeam.RovoChat,
	[ExperienceName.LOAD_STICKY_SKILL_LISTENER]: OwningTeam.RovoChat,
	[ExperienceName.MY_PROJECTS_LIST_QUERY]: OwningTeam.RovoChat,
	[ExperienceName.PREFETCH_CHAT]: OwningTeam.RovoChat,
	[ExperienceName.PUBSUB_LISTENER]: OwningTeam.RovoChat,
	[ExperienceName.RECONNECT_TO_ACTIVE_STREAM]: OwningTeam.RovoChat,
	[ExperienceName.SEND_A2UI_ACTION]: OwningTeam.RovoChat,
	[ExperienceName.SEND_DR_MESSAGE]: OwningTeam.RovoChat,
	[ExperienceName.SEND_MESSAGE_ROVO_CUSTOM_AGENT]: OwningTeam.RovoChat,
	[ExperienceName.SEND_TD_MESSAGE]: OwningTeam.RovoChat,
	[ExperienceName.SET_AUTO_APPLY_STATE]: OwningTeam.RovoChat,
	[ExperienceName.THREADS_LIST_QUERY]: OwningTeam.RovoChat,
	[ExperienceName.UPDATE_CONVERSATION_CHANNEL]: OwningTeam.RovoChat,
	[ExperienceName.UPLOAD_FILES_TO_CONVERSATION]: OwningTeam.RovoChat,
	[ExperienceName.VALIDATE_FILES]: OwningTeam.RovoChat,
	[ExperienceName.WARM_CONTEXT_BANNER_LINK]: OwningTeam.RovoChat,
	[ExperienceName.WARM_CONVERSATION_CACHE]: OwningTeam.RovoChat,
	[ExperienceName.SEND_MESSAGE]: OwningTeam.RovoChat,
	[ExperienceName.SEND_TASK_MODE_MESSAGE]: OwningTeam.RovoChat,
	[ExperienceName.SEND_CONFIRMED_TOOLS_TASK_MODE_MESSAGE]: OwningTeam.RovoChat,
	[ExperienceName.LOAD_AUTO_SUGGEST_SKILLS_UNIFIED]: OwningTeam.RovoChat,
	[ExperienceName.LOAD_CHAT_PROMPT_INPUT_SKILLS]: OwningTeam.RovoChat,
	[ExperienceName.LOAD_INLINE_CHAT_PROMPT_INPUT_SKILLS]: OwningTeam.RovoChat,
	[ExperienceName.SHARE_CONVERSATION_SESSION]: OwningTeam.RovoChat,
	[ExperienceName.MANAGE_CONVERSATION_SHARES]: OwningTeam.RovoChat,
	[ExperienceName.VIEW_CONVERSATION_SHARE]: OwningTeam.RovoChat,
	[ExperienceName.SHARE_ARTIFACT_ACCESS_CHANGE]: OwningTeam.RovoChat,
	[ExperienceName.SHARE_ARTIFACT_SHARE_AUDIENCES]: OwningTeam.RovoChat,
	[ExperienceName.SHARE_ARTIFACT_REVOKE_AUDIENCES]: OwningTeam.RovoChat,
	[ExperienceName.DOWNLOAD_ARTIFACT]: OwningTeam.RovoChat,
	[ExperienceName.PREVIEW_ARTIFACT_SMART_LINK_RESOLVED]: OwningTeam.RovoChat,
	// Jira AIFC experiences
	[ExperienceName.JIRA_RESOLVE_DRAFT_WORK_ITEM]: OwningTeam.GravityAi,
	[ExperienceName.JIRA_SAVE_DRAFT_WORK_ITEM]: OwningTeam.JiraAifc,
	[ExperienceName.JIRA_CREATE_WORK_ITEMS_BULK]: OwningTeam.JiraAifc,
	[ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_CREATE]: OwningTeam.JiraAifc,
	[ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_CREATE]: OwningTeam.JiraAifc,
	[ExperienceName.ROVO_SKILLS_PLAN_WORK_ITEM_UPDATE]: OwningTeam.Pulsar,
	[ExperienceName.ROVO_SKILLS_EXECUTION_WORK_ITEM_UPDATE]: OwningTeam.Pulsar,
	// Agent experiences
	[ExperienceName.AGENTS_FULLSCREEN_AGENT_LIST]: OwningTeam.RovoAgents,
	[ExperienceName.AGENTS_MODAL_AND_SIDEBAR_LIST]: OwningTeam.RovoAgents,
	[ExperienceName.AGENTS_MODAL_FAVOURITE_AGENTS]: OwningTeam.RovoAgents,
	[ExperienceName.DELETE_AGENT]: OwningTeam.RovoAgents,
	[ExperienceName.DUPLICATE_AGENT]: OwningTeam.RovoAgents,
	[ExperienceName.FAVOURITE_AGENT]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENTS]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENTS_OVERRIDES]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENTS_PAGINATED]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENT_ACTIONS]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENT_BY_AGENT_ID]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENT_BY_EXTERNAL_CONFIG_REFERENCE]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENT_BY_IDENTITY_ACCOUNT_ID]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENT_DEBUG_MODAL_CONVERSATION_REVIEW]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENT_SITE_PERMISSIONS]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENT_TEAM_DATA]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_AGENT_TENANT_DATA]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES]: OwningTeam.RovoAgents,
	[ExperienceName.FETCH_SCENARIO_LIST]: OwningTeam.RovoAgents,
	[ExperienceName.GENERATE_CONVERSATION_STARTERS]: OwningTeam.RovoAgents,
	[ExperienceName.GET_AGENT_PERMISSIONS]: OwningTeam.RovoAgents,
	[ExperienceName.GET_RECOMMENDED_AGENTS]: OwningTeam.RovoAgents,
	[ExperienceName.HYDRATE_AGENT_DATA]: OwningTeam.RovoAgents,
	[ExperienceName.LOAD_BROWSE_AGENTS]: OwningTeam.RovoAgents,
	[ExperienceName.LOAD_SKILLS_DIRECTORY_PRODUCTS_FILTER]: OwningTeam.RovoAgents,
	[ExperienceName.LOAD_SKILL_DETAIL_IN_SKILL_DIRECTORY]: OwningTeam.RovoAgents,
	[ExperienceName.OPEN_RECOMMENDED_SPACES]: OwningTeam.RovoAgents,
	[ExperienceName.PREFETCH_RECOMMENDED_SPACES]: OwningTeam.RovoAgents,
	[ExperienceName.SEARCH_BROWSE_AGENTS]: OwningTeam.RovoAgents,
	[ExperienceName.SEND_MESSAGE_NL_AGENT]: OwningTeam.RovoAgents,
	[ExperienceName.STUDIO_CREATE_AGENT_TRIGGER]: OwningTeam.RovoAgents,
	[ExperienceName.STUDIO_KNOWLEDGE_CONFIGURATION_VIEW]: OwningTeam.RovoAgents,
	[ExperienceName.STUDIO_KNOWLEDGE_MODAL_LOAD]: OwningTeam.RovoAgents,
	[ExperienceName.STUDIO_LOAD_AGENT_CARD]: OwningTeam.RovoAgents,
	[ExperienceName.STUDIO_LOAD_AGENT_EDITOR]: OwningTeam.RovoAgents,
	[ExperienceName.STUDIO_REMOVE_AGENT_TRIGGER]: OwningTeam.RovoAgents,
	[ExperienceName.STUDIO_SET_AGENT_TRIGGER_ENABLED]: OwningTeam.RovoAgents,
	[ExperienceName.STUDIO_UPDATE_AGENT_TRIGGER]: OwningTeam.RovoAgents,
	[ExperienceName.UNFAVOURITE_AGENT]: OwningTeam.RovoAgents,
	// CSM-specific experiences
	[ExperienceName.CSM_AGENT_IDENTITY_COMPANY]: OwningTeam.CsmAi,
	[ExperienceName.CSM_AGENT_IDENTITY_CONVERSATION_STARTERS]: OwningTeam.CsmAi,
	[ExperienceName.CSM_AGENT_IDENTITY_GREETING_MESSAGE]: OwningTeam.CsmAi,
	[ExperienceName.CSM_AGENT_IDENTITY_NAME]: OwningTeam.CsmAi,
	[ExperienceName.CSM_AGENT_IDENTITY_TONE]: OwningTeam.CsmAi,
	[ExperienceName.CSM_WIDGET_RENDER]: OwningTeam.CsmAi,
	// Additional confirmed experience owners
	[ExperienceName.FETCH_CHAT]: OwningTeam.RovoChat,
	[ExperienceName.FETCH_INSIGHTS_STATUS]: OwningTeam.RovoExpansion,
	[ExperienceName.FETCH_INSIGHTS_FEED]: OwningTeam.RovoExpansion,
	[ExperienceName.STUDIO_CREATE_AGENT]: OwningTeam.JiraWarepil,
	[ExperienceName.STUDIO_CREATE_AGENT_ASSIGN]: OwningTeam.JiraWarepil,
	[ExperienceName.STUDIO_CREATE_AGENT_INVOKE]: OwningTeam.JiraWarepil,
	[ExperienceName.STUDIO_CREATE_AGENT_BUILD_AND_CHAT]: OwningTeam.JiraWarepil,
	[ExperienceName.STUDIO_CREATE_AGENT_REFINE_IN_STUDIO]: OwningTeam.JiraWarepil,
	[ExperienceName.STUDIO_CREATE_AGENT_RENDER]: OwningTeam.JiraWarepil,

	// Ownership is not confirmed for these experiences.
	[ExperienceName.LOAD_PANEL]: OwningTeam.Unassigned,
	[ExperienceName.LOAD_CREATE_AGENT]: OwningTeam.Unassigned,
	[ExperienceName.FETCH_AGENT_DEBUG_MODAL_DATA]: OwningTeam.Unassigned,
	[ExperienceName.CREATE_AGENT]: OwningTeam.Unassigned,
	[ExperienceName.UPDATE_AGENT]: OwningTeam.Unassigned,
	[ExperienceName.SEND_CONFIRMED_TOOLS_MESSAGE]: OwningTeam.Unassigned,
	[ExperienceName.ABORT_AGENT_STREAM]: OwningTeam.Unassigned,
	[ExperienceName.GET_CONVERSATION_CHANNELS]: OwningTeam.Unassigned,
	[ExperienceName.RESOLVE_CONVERSATION_ACTION]: OwningTeam.Unassigned,
	[ExperienceName.MESSAGE_RENDERER]: OwningTeam.Unassigned,
	[ExperienceName.FETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE]: OwningTeam.Unassigned,
	[ExperienceName.PREFETCH_CONNECTED_KNOWLEDGE_SOURCES_STORE]: OwningTeam.Unassigned,
	[ExperienceName.CHANGE_AGENT_OWNER]: OwningTeam.Unassigned,
	[ExperienceName.JIRA_PREVIEW_CARD_RENDER]: OwningTeam.Unassigned,
	[ExperienceName.JSM_PREVIEW_CARD_RENDER]: OwningTeam.Unassigned,
	[ExperienceName.PREFETCH_AGENT_SITE_PERMISSIONS]: OwningTeam.Unassigned,
	[ExperienceName.FILTERED_CLIENT_ERROR]: OwningTeam.Unassigned,
	[ExperienceName.PREFETCH_RECOMMENDED_AGENTS]: OwningTeam.Unassigned,
	[ExperienceName.GET_CONVERSATION_FILES]: OwningTeam.Unassigned,
	[ExperienceName.CSM_AGENT_IDENTITY_GENERATE_CONVERSATION_STARTERS]: OwningTeam.Unassigned,
	[ExperienceName.STUDIO_TRIGGER_SOLUTION_PLAN]: OwningTeam.Unassigned,
	[ExperienceName.STUDIO_UPDATE_AGENT]: OwningTeam.Unassigned,
	[ExperienceName.STUDIO_AUTOMATION_CREATE_RULE]: OwningTeam.Unassigned,
	[ExperienceName.SHARE_ARTIFACT]: OwningTeam.Unassigned,
	[ExperienceName.PROACTIVE_ACTION_ITEMS_LOAD]: OwningTeam.Unassigned,
	[ExperienceName.PROACTIVE_ACTION_ITEMS_INITIAL_RETRIEVE]: OwningTeam.Unassigned,
	[ExperienceName.PROACTIVE_ACTION_ITEMS_GENERATE]: OwningTeam.Unassigned,
	[ExperienceName.PROACTIVE_ACTION_ITEMS_DISMISS]: OwningTeam.Unassigned,
	[ExperienceName.PINNED_ITEMS_LIST_LOAD]: OwningTeam.Unassigned,
	[ExperienceName.SUGGESTED_WORK_LOAD]: OwningTeam.Unassigned,
	[ExperienceName.INFERRED_PROJECTS_LOAD]: OwningTeam.Unassigned,
	[ExperienceName.INFERRED_PROJECT_CONTEXT_LOAD]: OwningTeam.Unassigned,
};

/** Returns the mapped owner, including Unassigned for unregistered runtime names. */
export const getOwningTeam = (experienceName: string): OwningTeam =>
	Object.prototype.hasOwnProperty.call(DEFAULT_TEAM_FOR_EXPERIENCE, experienceName)
		? DEFAULT_TEAM_FOR_EXPERIENCE[experienceName as ExperienceNameType]
		: OwningTeam.Unassigned;
